import { useMutation, useQueryClient } from '@tanstack/react-query'
import { subscriptionQuery, subscriptionsQuery } from '../../account/queries'
import type { Household } from '../../api/households'
import {
  type Subscription,
  type SubscriptionChange,
  updateSubscription,
} from '../../api/subscriptions'
import { track } from '../../runtime/analytics'
import { currentAuth } from '../../state/session'

export interface SubscriptionUpdate {
  subscription: Subscription
  change: SubscriptionChange
}

/**
 * Ask for a change to one of the household's subscriptions and keep every
 * view of it in step. What the API returns is Recharge's word on the
 * subscription now, with its next charge, so it is written straight into the
 * cache — the subscription's own query and the household list the cards
 * choose from — and nothing refetches after a save. The address book is the
 * address editor's to refresh, once every move it asked for has landed.
 */
/**
 * What a change is called in analytics, with nothing of whose subscription it
 * is. An address change is the address editor's to tell of, once per save,
 * however many kits it moves.
 */
function trackChange(change: SubscriptionChange): void {
  if ('shippingAddressId' in change) return
  if ('skipNextCharge' in change) track({ kind: 'subscription_change', action: 'skip' })
  else if (change.status === 'paused') track({ kind: 'subscription_change', action: 'pause' })
  else if (change.status === 'active') track({ kind: 'subscription_change', action: 'resume' })
  else track({ kind: 'subscription_change', action: 'cancel', reason: change.reason })
}

export function useUpdateSubscription(household: Household) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ subscription, change }: SubscriptionUpdate) =>
      updateSubscription(subscription.id, change, { auth: currentAuth() }),
    onSuccess: (updated, { change }) => {
      trackChange(change)
      queryClient.setQueryData(subscriptionQuery(updated.id).queryKey, updated)
      queryClient.setQueryData(subscriptionsQuery(household).queryKey, (subscriptions) =>
        subscriptions?.map((subscription) =>
          subscription.id === updated.id ? updated : subscription,
        ),
      )
    },
  })
}
